import { useEffect, useRef } from "react"
import { Megaphone } from "lucide-react"
import type { BreakoutBroadcastMessage } from "@/features/meeting/store/breakout-broadcast.store"

/**
 * The broadcast conversation itself.
 *
 * Shared by the host's composer screen and the participant's read-only viewer,
 * so the two cannot drift into showing the same thread differently — the whole
 * point of the participant's screen is that it is what the host sent.
 */
export function BreakoutBroadcastThread({
  messages,
  emptyHint,
  /** This client's peer id. Decides which side each message sits on. */
  myPeerId,
}: {
  messages: BreakoutBroadcastMessage[]
  emptyHint?: string
  myPeerId?: string | null
}) {
  const endRef = useRef<HTMLDivElement | null>(null)

  // Newest message in view. A broadcast thread is read from the bottom: the one
  // that just arrived is the one that matters.
  useEffect(() => {
    endRef.current?.scrollIntoView({ block: "end" })
  }, [messages.length])

  if (messages.length === 0) {
    return (
      <div className="flex min-h-0 flex-1 flex-col items-center justify-center gap-2 px-8 text-center">
        <div className="grid h-12 w-12 place-items-center rounded-full bg-slate-100 dark:bg-muted">
          <Megaphone className="h-5 w-5 text-slate-400 dark:text-muted-foreground" />
        </div>
        <p className="text-sm font-medium text-slate-600 dark:text-foreground">No broadcasts yet</p>
        {emptyHint && (
          <p className="max-w-xs text-xs text-slate-400 dark:text-muted-foreground">{emptyHint}</p>
        )}
      </div>
    )
  }

  return (
    <div className="min-h-0 flex-1 space-y-3 overflow-y-auto px-4 py-4">
      {messages.map((m) => {
        // By id, not by name. Host and co-host can both broadcast and may share
        // a display name, and a host who renames themselves would otherwise
        // change the side of messages they had already sent.
        const mine = !!myPeerId && m.senderId === myPeerId
        return (
          <div key={m.id} className={`flex flex-col ${mine ? "items-end" : "items-start"}`}>
            <div
              className={`max-w-[85%] px-3.5 py-2 ${
                mine
                  ? "rounded-2xl rounded-tr-sm bg-primary text-white"
                  : "rounded-2xl rounded-tl-sm border border-amber-200/70 bg-amber-50 dark:border-amber-900/40 dark:bg-amber-950/25"
              }`}
            >
              <div className="mb-0.5 flex items-center gap-1.5">
                <Megaphone
                  className={`h-3 w-3 shrink-0 ${mine ? "text-white/80" : "text-amber-600"}`}
                />
                <span
                  className={`text-[11px] font-semibold ${
                    mine ? "text-white/90" : "text-amber-700 dark:text-amber-400"
                  }`}
                >
                  {/* Your own name is not information you need on your own
                      message — and with several hosts in the room, seeing your
                      own name in the sender slot reads as somebody else. */}
                  {mine ? "You" : m.senderName || "Host"}
                </span>
              </div>
              <p
                className={`whitespace-pre-wrap break-words text-sm ${
                  mine ? "text-white" : "text-slate-800 dark:text-foreground"
                }`}
              >
                {m.text}
              </p>
            </div>
            <div className={`mt-1 flex flex-wrap items-center gap-1.5 ${mine ? "pr-1" : "pl-1"}`}>
              <span className="text-[10px] text-slate-400 dark:text-muted-foreground">
                {new Date(m.sentAt).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
              </span>
              <span className="text-[10px] text-slate-400 dark:text-muted-foreground">
                · {describeAudience(m)}
              </span>
            </div>
          </div>
        )
      })}
      <div ref={endRef} />
    </div>
  )
}

/**
 * Who a message went to.
 *
 * Names when we have them, a count when we do not. Falling back to the raw ids
 * would be worse than the count — they are uuids, and a recipient cannot turn
 * one into a room.
 */
function describeAudience(m: BreakoutBroadcastMessage): string {
  if (m.roomIds.length === 0) return "All rooms"
  if (m.roomNames.length > 0) return m.roomNames.join(", ")
  return m.roomIds.length === 1 ? "1 room" : `${m.roomIds.length} rooms`
}
